import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Testimonial {
  name: string;
  office: string;
  photo: string;
  quote: string;
  metric?: string;
}

const CONTENT: Testimonial[] = [
  {
    name: "Douglas Carvalho",
    office: "Assessor parceiro · 1 ano de Allu",
    photo: "/selecao/depoimentos/assessor-douglas.jpeg",
    quote:
      "Já sou parceiro Allu há 1 ano e recentemente fui convidado a participar das vendas externas. Depois de visitar a Allu e ver o time trabalhando e todo o comprometimento, fiquei ainda mais seguro de fazer parte. Todos os investidores que captei estão satisfeitos, e muitos investindo continuamente.",
    metric: "Parceiro desde 2025",
  },
  {
    name: "Luis Medeiros",
    office: "LH Capital",
    photo: "/selecao/depoimentos/assessor-luis.jpeg",
    quote:
      "Minha experiência como consultor externo parceiro da Allu Invest tem sido excepcional e estratégica para a LH Capital. A consistência e os resultados nos dão tranquilidade para ofertar os produtos aos nossos clientes, que buscam performance e diversificação. Uma parceria sólida, pautada pela eficiência e pela geração de valor real para os investidores.",
    metric: "Consultor parceiro · LH Capital",
  },
];

const TestimonialCard = ({ t }: { t: Testimonial }) => (
  <>
    <blockquote className="text-[15px] sm:text-base text-zinc-200 leading-relaxed">
      <span
        aria-hidden="true"
        className="text-3xl text-white/30 leading-none align-top mr-1"
      >
        “
      </span>
      {t.quote}
    </blockquote>

    <div className="mt-7 flex items-center gap-3.5">
      <div className="w-12 h-12 rounded-full overflow-hidden bg-white/10 shrink-0 ring-1 ring-white/15">
        <img
          src={t.photo}
          alt=""
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-white truncate">
          {t.name}
        </div>
        <div className="text-xs text-zinc-400 truncate">{t.office}</div>
      </div>
    </div>
  </>
);

export const EventoDepoimentosAssessor = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const cards = el.querySelectorAll<HTMLElement>("[data-card]");
      const center = el.scrollLeft + el.clientWidth / 2;
      let closestIdx = 0;
      let closestDist = Infinity;
      cards.forEach((card, i) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const dist = Math.abs(cardCenter - center);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      });
      setCurrentIndex(closestIdx);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToIndex = (i: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelectorAll<HTMLElement>("[data-card]")[i];
    if (!card) return;
    el.scrollTo({
      left: card.offsetLeft - el.clientWidth / 2 + card.offsetWidth / 2,
      behavior: "smooth",
    });
  };

  return (
    <section id="depoimentos" className="bg-[#112711] scroll-mt-24">
      <div className="container-wide py-12 sm:py-16">
        <div className="max-w-3xl mb-7 sm:mb-9">
          <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-zinc-400">
            Assessores parceiros
          </div>
          <h2
            className="mt-3 text-2xl sm:text-3xl md:text-4xl font-medium text-white leading-snug"
            style={{
              fontFamily:
                '"Playfair Display", Georgia, "Times New Roman", serif',
              letterSpacing: "-0.02em",
            }}
          >
            Quem já está divulgando conta o que mudou.
          </h2>
        </div>

        {/* Mobile: carrossel */}
        <div className="md:hidden">
          <div
            ref={scrollRef}
            className="no-scrollbar flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth -mx-4 px-4"
          >
            {CONTENT.map((t, i) => (
              <article
                key={i}
                data-card
                className="shrink-0 w-[calc(100vw-2.5rem)] snap-center"
              >
                <TestimonialCard t={t} />
              </article>
            ))}
          </div>

          <div className="flex items-center justify-center gap-4 mt-5">
            <button
              type="button"
              onClick={() => scrollToIndex(Math.max(0, currentIndex - 1))}
              disabled={currentIndex === 0}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 ring-1 ring-white/15 text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
              aria-label="Depoimento anterior"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-1.5">
              {CONTENT.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => scrollToIndex(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    currentIndex === i ? "w-5 bg-[#4BD084]" : "w-2 bg-white/20"
                  }`}
                  aria-label={`Ir para depoimento ${i + 1}`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() =>
                scrollToIndex(Math.min(CONTENT.length - 1, currentIndex + 1))
              }
              disabled={currentIndex === CONTENT.length - 1}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 ring-1 ring-white/15 text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
              aria-label="Próximo depoimento"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Desktop: 2 colunas lado a lado */}
        <div className="hidden md:grid md:grid-cols-2 md:gap-x-12 lg:gap-x-20 md:divide-x md:divide-white/10">
          {CONTENT.map((t, i) => (
            <article key={i} className={i === 0 ? "" : "md:pl-12 lg:pl-20"}>
              <TestimonialCard t={t} />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
